import { useContext, useEffect, useState } from 'react';
import { FileText, FileWarning, Loader2, Pencil, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { GlobalContext } from '../../context/GlobalContext';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import Input from '../../components/Input';
import Notice from '../../components/Notice';
import Select from '../../components/Select';
import Spinner from '../../components/Spinner';
import { Button } from '../../shadcn/Button';
import { cn } from '../../shadcn/utils';
import { callAPI, InsuranceClaimRow, InsurancePolicyRow } from '../../hooks/api';
import { COUNTRY_LABELS } from '../../utils/constants';

type PolicyStatusFilter = 'all' | 'active' | 'inactive';
type ClaimStatusFilter = 'all' | 'RECIBIDO' | 'EN_ANALISIS' | 'APROBADO' | 'RECHAZADO' | 'PENDIENTE_INFO';
type ClaimDecisionStatus = 'APROBADO' | 'RECHAZADO';
type DateSort = 'desc' | 'asc';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [20, 50, 100];

const POLICY_FILTER_OPTIONS: { label: string; value: PolicyStatusFilter }[] = [
	{ label: 'Todas', value: 'all' },
	{ label: 'Activas', value: 'active' },
	{ label: 'Inactivas', value: 'inactive' }
];

const CLAIM_FILTER_OPTIONS: { label: string; value: ClaimStatusFilter }[] = [
	{ label: 'Todos', value: 'all' },
	{ label: 'Recibido', value: 'RECIBIDO' },
	{ label: 'En análisis', value: 'EN_ANALISIS' },
	{ label: 'Pendiente info', value: 'PENDIENTE_INFO' },
	{ label: 'Aprobado', value: 'APROBADO' },
	{ label: 'Rechazado', value: 'RECHAZADO' }
];

const SORT_OPTIONS: { label: string; value: DateSort }[] = [
	{ label: 'Más recientes', value: 'desc' },
	{ label: 'Más antiguas', value: 'asc' }
];

const CLAIM_STATUS_LABELS: Record<ClaimDecisionStatus, string> = {
	APROBADO: 'Aprobado',
	RECHAZADO: 'Rechazado'
};

const EMPTY_VALUE = '—';

export default function InsurancePolicies() {
	const { userData, internalCountry } = useContext(GlobalContext);
	const country = internalCountry.get() || userData?.country || '';
	const countryLabel = getCountryLabel(country);

	const [policies, setPolicies] = useState<InsurancePolicyRow[]>([]);
	const [claims, setClaims] = useState<InsuranceClaimRow[]>([]);
	const [policyCursor, setPolicyCursor] = useState<string | null>(null);
	const [claimCursor, setClaimCursor] = useState<string | null>(null);
	const [policyPageCursors, setPolicyPageCursors] = useState<Array<string | null>>([null]);
	const [claimPageCursors, setClaimPageCursors] = useState<Array<string | null>>([null]);
	const [policyPageIndex, setPolicyPageIndex] = useState(0);
	const [claimPageIndex, setClaimPageIndex] = useState(0);
	const [policiesHasMore, setPoliciesHasMore] = useState(false);
	const [claimsHasMore, setClaimsHasMore] = useState(false);
	const [policiesLoading, setPoliciesLoading] = useState(true);
	const [claimsLoading, setClaimsLoading] = useState(true);
	const [claimUpdatingId, setClaimUpdatingId] = useState<string | null>(null);
	const [claimLetterGeneratingId, setClaimLetterGeneratingId] = useState<string | null>(null);
	const [pendingClaimAction, setPendingClaimAction] = useState<{ claim: InsuranceClaimRow; status: ClaimDecisionStatus } | null>(null);
	const [policySearchPending, setPolicySearchPending] = useState(false);
	const [claimSearchPending, setClaimSearchPending] = useState(false);
	const [error, setError] = useState('');
	const [policyError, setPolicyError] = useState('');
	const [refreshKey, setRefreshKey] = useState(0);
	const [policyRefreshKey, setPolicyRefreshKey] = useState(0);
	const [policySearchInput, setPolicySearchInput] = useState('');
	const [policySearch, setPolicySearch] = useState('');
	const [policyStatusFilter, setPolicyStatusFilter] = useState<PolicyStatusFilter>('all');
	const [policySort, setPolicySort] = useState<DateSort>('desc');
	const [policyPageSize, setPolicyPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [claimSearchInput, setClaimSearchInput] = useState('');
	const [claimSearch, setClaimSearch] = useState('');
	const [claimStatusFilter, setClaimStatusFilter] = useState<ClaimStatusFilter>('all');
	const [claimSort, setClaimSort] = useState<DateSort>('desc');
	const [claimPageSize, setClaimPageSize] = useState(DEFAULT_PAGE_SIZE);

	const fetchPolicies = async ({ cursor, pageIndex, pageSize }: { cursor: string | null; pageIndex: number; pageSize: number }) => {
		if (!country || !userData) {
			setPolicySearchPending(false);
			return;
		}

		setPoliciesLoading(true);
		setPolicyError('');
		const [response, error] = await callAPI(
			'https://api.krece.app/tables/insurance_policies/',
			'POST',
			{
				country,
				status_filter: policyStatusFilter,
				search: policySearch,
				order: policySort,
				last_object_id: cursor,
				page_index: pageIndex,
				batch_size: pageSize
			},
			userData,
			country
		);

		if (error || !response) {
			setPolicies([]);
			setPoliciesHasMore(false);
			setPolicyError(error || 'Intente nuevamente.');
		} else {
			setPolicies(response.policies.slice(0, pageSize));
			setPolicyCursor(response.last_object_id);
			setPoliciesHasMore(response.has_more);
			setPolicyPageIndex(pageIndex);
		}
		setPoliciesLoading(false);
		setPolicySearchPending(false);
	};

	const fetchClaims = async ({ cursor, pageIndex, pageSize }: { cursor: string | null; pageIndex: number; pageSize: number }) => {
		if (!country || !userData) {
			setClaimSearchPending(false);
			return;
		}

		setClaimsLoading(true);
		setError('');
		const [response, error] = await callAPI(
			'https://api.krece.app/tables/insurance_claims/',
			'POST',
			{
				country,
				status_filter: claimStatusFilter,
				search: claimSearch,
				order: claimSort,
				last_object_id: cursor,
				page_index: pageIndex,
				batch_size: pageSize
			},
			userData,
			country
		);

		if (error || !response) {
			setClaims([]);
			setError(error || 'Ha ocurrido un error. Intente más tarde.');
		} else {
			setClaims(response.claims.slice(0, pageSize));
			setClaimCursor(response.last_object_id);
			setClaimsHasMore(response.has_more);
			setClaimPageIndex(pageIndex);
		}
		setClaimsLoading(false);
		setClaimSearchPending(false);
	};

	const handlePolicySearchSubmit = (value: string) => {
		const nextSearch = value.trim();
		setPolicySearchPending(nextSearch !== policySearch);
		setPolicySearch(nextSearch);
	};

	const handleClaimSearchSubmit = (value: string) => {
		const nextSearch = value.trim();
		setClaimSearchPending(nextSearch !== claimSearch);
		setClaimSearch(nextSearch);
	};

	const handleNextPoliciesPage = () => {
		if (!policiesHasMore || !policyCursor || policiesLoading) return;
		const nextPageIndex = policyPageIndex + 1;
		setPolicyPageCursors((previous) => {
			const cursors = previous.slice(0, nextPageIndex);
			cursors[nextPageIndex] = policyCursor;
			return cursors;
		});
		fetchPolicies({ cursor: policyCursor, pageIndex: nextPageIndex, pageSize: policyPageSize });
	};

	const handlePreviousPoliciesPage = () => {
		if (policyPageIndex === 0 || policiesLoading) return;
		const previousPageIndex = policyPageIndex - 1;
		fetchPolicies({ cursor: policyPageCursors[previousPageIndex] || null, pageIndex: previousPageIndex, pageSize: policyPageSize });
	};

	const handleNextClaimsPage = () => {
		if (!claimsHasMore || !claimCursor || claimsLoading) return;
		const nextPageIndex = claimPageIndex + 1;
		setClaimPageCursors((previous) => {
			const cursors = previous.slice(0, nextPageIndex);
			cursors[nextPageIndex] = claimCursor;
			return cursors;
		});
		fetchClaims({ cursor: claimCursor, pageIndex: nextPageIndex, pageSize: claimPageSize });
	};

	const handlePreviousClaimsPage = () => {
		if (claimPageIndex === 0 || claimsLoading) return;
		const previousPageIndex = claimPageIndex - 1;
		fetchClaims({ cursor: claimPageCursors[previousPageIndex] || null, pageIndex: previousPageIndex, pageSize: claimPageSize });
	};

	const handlePolicyPageSizeChange = (value: string | boolean | null) => {
		setPolicyPageSize(Number(value || DEFAULT_PAGE_SIZE));
	};

	const handleClaimPageSizeChange = (value: string | boolean | null) => {
		setClaimPageSize(Number(value || DEFAULT_PAGE_SIZE));
	};

	const handleUpdateClaimStatus = (claim: InsuranceClaimRow, status: ClaimDecisionStatus) => {
		setPendingClaimAction({ claim, status });
	};

	const handleConfirmClaimStatusUpdate = async () => {
		if (!userData || !pendingClaimAction) return 'No se pudo actualizar el siniestro.';
		const { claim, status } = pendingClaimAction;
		const actionLabel = status === 'APROBADO' ? 'aprobar' : 'rechazar';

		setClaimUpdatingId(claim.id);
		setClaimsLoading(true);
		setError('');
		const [response, error] = await callAPI(
			'https://api.krece.app/update_siniestro_status/',
			'POST',
			{ claim_id: claim.id || claim.siniestroId, status },
			userData,
			country
		);

		setClaimUpdatingId(null);
		setClaimsLoading(false);
		if (error || !response) return error || `No se pudo ${actionLabel} el siniestro.`;

		setClaims((currentClaims) =>
			currentClaims.map((currentClaim) =>
				currentClaim.id === claim.id
					? {
							...currentClaim,
							status,
							statusLabel: CLAIM_STATUS_LABELS[status],
							resolvedAt: new Date().toISOString()
						}
					: currentClaim
			)
		);
		return true;
	};

	const handleDownloadClaimLetter = async (claim: InsuranceClaimRow) => {
		if (!userData) return;
		setClaimLetterGeneratingId(claim.id);
		setError('');
		const [response, error] = await callAPI(
			'https://api.krece.app/siniestro_desincorporation_letter/',
			'POST',
			{ claim_id: claim.id || claim.siniestroId },
			userData,
			country
		);
		setClaimLetterGeneratingId(null);

		if (error || !response) {
			setError(error || 'No se pudo generar la carta de desincorporación.');
			return;
		}
		downloadBase64File(response.pdf_base64, response.filename, response.content_type);
	};

	useEffect(() => {
		const timeoutId = window.setTimeout(() => {
			setPolicyCursor(null);
			setPolicyPageCursors([null]);
			setPolicyPageIndex(0);
			setPoliciesHasMore(false);
			fetchPolicies({ cursor: null, pageIndex: 0, pageSize: policyPageSize });
		}, 250);

		return () => window.clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [country, policyPageSize, policyRefreshKey, policySearch, policySort, policyStatusFilter, refreshKey, userData]);

	useEffect(() => {
		const timeoutId = window.setTimeout(() => {
			setClaimCursor(null);
			setClaimPageCursors([null]);
			setClaimPageIndex(0);
			setClaimsHasMore(false);
			fetchClaims({ cursor: null, pageIndex: 0, pageSize: claimPageSize });
		}, 250);

		return () => window.clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [claimPageSize, claimSearch, claimSort, claimStatusFilter, country, refreshKey, userData]);

	const refreshing = policiesLoading || claimsLoading;
	const policySearchLoading = policySearchPending || (policiesLoading && Boolean(policySearch.trim()));
	const claimSearchLoading = claimSearchPending || (claimsLoading && Boolean(claimSearch.trim()));

	return (
		<div className="mx-auto w-full max-w-7xl space-y-6 p-4 md:p-[1.25rem]">
			<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div className="space-y-1">
					<h2 className="text-2xl font-semibold text-neutral-100">Pólizas de seguro</h2>
					<p className="text-sm text-neutral-400">
						Administración de pólizas y siniestros para <span className="font-medium text-neutral-200">{countryLabel}</span>
					</p>
				</div>
				<Button
					className="!w-auto"
					disabled={refreshing}
					Icon={<RefreshCw className={cn('size-4', refreshing && 'animate-spin')} />}
					onClick={() => setRefreshKey((value) => value + 1)}
				>
					Actualizar
				</Button>
			</div>

			{error && <Notice message={error} variant="error" />}

			{pendingClaimAction && (
				<ConfirmDialog
					title={pendingClaimAction.status === 'APROBADO' ? 'Aceptar siniestro' : 'Rechazar siniestro'}
					message={
						pendingClaimAction.status === 'APROBADO'
							? '¿Seguro que deseas aprobar este siniestro? El equipo será enviado a bloquear nuevamente y las cuotas pendientes se marcarán como pagadas por la aseguradora.'
							: '¿Seguro que deseas rechazar este siniestro?'
					}
					onClose={() => setPendingClaimAction(null)}
					onConfirm={handleConfirmClaimStatusUpdate}
				/>
			)}

			<section className="space-y-3">
						<SectionHeading
							title="Pólizas"
							subtitle={
								policiesLoading ? 'Cargando pólizas...' : policyError ? 'Consulta fallida' : `${policies.length} pólizas en esta página`
							}
						/>
						<div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_180px_180px]">
							<Input
								disabled={policiesLoading}
								placeholder="Buscar por cédula, correo, póliza, certificado, tienda, equipo o IMEI"
								floatingLabel="Buscar pólizas"
								value={policySearchInput}
								onChange={setPolicySearchInput}
								onSubmit={handlePolicySearchSubmit}
								Icon={
									policySearchLoading ? (
										<Loader2 className="size-4 animate-spin text-primary-400" />
									) : (
										<Search className="size-4 text-neutral-500" />
									)
								}
							/>
							<Select
								disabled={policiesLoading}
								floatingLabel="Estado"
								options={POLICY_FILTER_OPTIONS}
								defaultValue={policyStatusFilter}
								onSelectedChange={(value) => setPolicyStatusFilter((value || 'all') as PolicyStatusFilter)}
							/>
							<Select
								disabled={policiesLoading}
								floatingLabel="Orden por fecha"
								options={SORT_OPTIONS}
								defaultValue={policySort}
								onSelectedChange={(value) => setPolicySort((value || 'desc') as DateSort)}
							/>
						</div>
						{policyError ? (
							<div className="space-y-3">
								<Notice message={`No se pudieron consultar las pólizas. ${policyError}`} variant="error" />
								<Button className="!w-auto" variant="secondary" onClick={() => setPolicyRefreshKey((value) => value + 1)}>
									Reintentar
								</Button>
							</div>
						) : (
							<>
								{!policiesLoading && policiesHasMore && policies.length < policyPageSize && (
									<Notice
										message={
											'La consulta revisó solo una parte de los registros. Pulse Siguientes para continuar ' +
											'o busque por cédula, correo o certificado completo.'
										}
										variant="info"
									/>
								)}
								<PoliciesTable policies={policies} loading={policiesLoading} hasMore={policiesHasMore} />
								<PaginationFooter
									pageCountsOnly
									count={policies.length}
									pageIndex={policyPageIndex}
									pageSize={policyPageSize}
									hasMore={policiesHasMore}
									loading={policiesLoading}
									onPrevious={handlePreviousPoliciesPage}
									onNext={handleNextPoliciesPage}
									onPageSizeChange={handlePolicyPageSizeChange}
								/>
							</>
						)}
					</section>

					<section className="space-y-3">
						<SectionHeading title="Siniestros" subtitle={claimsLoading ? 'Cargando siniestros...' : getPageSubtitle(claims.length, claimPageIndex, claimPageSize, 'siniestros')} />
						<div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_180px_180px]">
							<Input
								disabled={claimsLoading}
								placeholder="Buscar por siniestro, póliza, cliente, equipo, lugar o descripción"
								floatingLabel="Buscar siniestros"
								value={claimSearchInput}
								onChange={setClaimSearchInput}
								onSubmit={handleClaimSearchSubmit}
								Icon={
									claimSearchLoading ? (
										<Loader2 className="size-4 animate-spin text-primary-400" />
									) : (
										<Search className="size-4 text-neutral-500" />
									)
								}
							/>
							<Select
								disabled={claimsLoading}
								floatingLabel="Estado"
								options={CLAIM_FILTER_OPTIONS}
								defaultValue={claimStatusFilter}
								onSelectedChange={(value) => setClaimStatusFilter((value || 'all') as ClaimStatusFilter)}
							/>
							<Select
								disabled={claimsLoading}
								floatingLabel="Orden por fecha"
								options={SORT_OPTIONS}
								defaultValue={claimSort}
								onSelectedChange={(value) => setClaimSort((value || 'desc') as DateSort)}
							/>
						</div>
						<ClaimsTable
							claims={claims}
							loading={claimsLoading}
							updatingClaimId={claimUpdatingId}
							letterGeneratingId={claimLetterGeneratingId}
							onUpdateStatus={handleUpdateClaimStatus}
							onDownloadLetter={handleDownloadClaimLetter}
						/>
						<PaginationFooter
							count={claims.length}
							pageIndex={claimPageIndex}
							pageSize={claimPageSize}
							hasMore={claimsHasMore}
							loading={claimsLoading}
							onPrevious={handlePreviousClaimsPage}
							onNext={handleNextClaimsPage}
							onPageSizeChange={handleClaimPageSizeChange}
						/>
			</section>
		</div>
	);
}

function SectionHeading({ title, subtitle }: { title: string; subtitle: string }) {
	return (
		<div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
			<h3 className="text-lg font-semibold text-neutral-100">{title}</h3>
			<p className="text-sm text-neutral-400">{subtitle}</p>
		</div>
	);
}

function PaginationFooter({
	count,
	pageIndex,
	pageSize,
	hasMore,
	loading,
	onPrevious,
	onNext,
	onPageSizeChange,
	pageCountsOnly = false
}: {
	count: number;
	pageIndex: number;
	pageSize: number;
	hasMore: boolean;
	loading: boolean;
	onPrevious: () => void;
	onNext: () => void;
	onPageSizeChange: (value: string | boolean | null) => void;
	pageCountsOnly?: boolean;
}) {
	if (!count && !hasMore && pageIndex === 0) return null;

	return (
		<div className="flex flex-col gap-3 rounded-lg border border-neutral-800 bg-neutral-900/50 px-4 py-3 text-sm text-neutral-400 sm:flex-row sm:items-center sm:justify-between">
			<span>
				{pageCountsOnly ? `Página ${pageIndex + 1}, ${count} resultados` : getPageSubtitle(count, pageIndex, pageSize, 'resultados')}
			</span>
			<div className="flex flex-wrap items-center gap-2">
				<Select
					disabled={loading}
					className="bg-neutral-1000 md:bg-neutral-950"
					options={PAGE_SIZE_OPTIONS.map((size) => ({ label: `${size} por página`, value: String(size) }))}
					defaultValue={String(pageSize)}
					isDefaultValueNull={false}
					onSelectedChange={onPageSizeChange}
				/>
				<Button className="!w-auto" disabled={pageIndex === 0 || loading} variant="secondary" onClick={onPrevious}>
					Anterior
				</Button>
				<Button className="!w-auto" disabled={!hasMore || loading} onClick={onNext}>
					Siguientes {pageSize}
				</Button>
			</div>
		</div>
	);
}

function PoliciesTable({ policies, loading, hasMore }: { policies: InsurancePolicyRow[]; loading: boolean; hasMore: boolean }) {
	if (!policies.length) {
		return (
			<div className="relative min-h-[260px] overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900/50">
				{!loading && (
					<EmptyState
						icon={ShieldCheck}
						title={hasMore ? 'No hay coincidencias en esta parte de la búsqueda' : 'No hay pólizas para mostrar'}
						subtitle={hasMore ? 'Pulse Siguientes para continuar revisando.' : 'Ajuste los filtros o cambie el país seleccionado.'}
					/>
				)}
				{loading && <TableLoadingOverlay />}
			</div>
		);
	}

	return (
		<div className="relative overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900/50">
			<div className="overflow-x-auto">
				<table className="w-full min-w-[1180px] text-left text-sm">
					<thead className="border-b border-neutral-800 bg-neutral-950 text-xs uppercase text-neutral-400">
						<tr>
							<TableHeader>Póliza</TableHeader>
							<TableHeader>Estado</TableHeader>
							<TableHeader>Fecha emisión</TableHeader>
							<TableHeader>Válida hasta</TableHeader>
							<TableHeader>Cliente</TableHeader>
							<TableHeader>Sucursal / vendedor</TableHeader>
							<TableHeader>Equipo</TableHeader>
							<TableHeader>Siniestro activo</TableHeader>
						</tr>
					</thead>
					<tbody className="divide-y divide-neutral-800/80">
						{policies.map((policy) => (
							<tr key={policy.id} className="text-neutral-200 transition-colors hover:bg-neutral-800/30">
								<TableCell>
									<div className="font-medium text-neutral-100">{policy.policyNumber || EMPTY_VALUE}</div>
									<div className="text-xs text-neutral-500">Cert. {policy.certificateNumber || EMPTY_VALUE}</div>
								</TableCell>
								<TableCell>
									<StatusBadge status={policy.status}>{policy.statusLabel}</StatusBadge>
								</TableCell>
								<TableCell>{formatDateTime(policy.issuedAt)}</TableCell>
								<TableCell>{formatDate(policy.validUntil)}</TableCell>
								<TableCell>
									<div className="max-w-[220px] truncate" title={policy.customerEmail}>
										{policy.customerEmail || EMPTY_VALUE}
									</div>
									<div className="text-xs text-neutral-500">{policy.deviceId}</div>
								</TableCell>
								<TableCell>
									<div className="max-w-[220px] truncate" title={policy.subStore}>
										{policy.subStore || policy.sellerStore || EMPTY_VALUE}
									</div>
									<div className="text-xs text-neutral-500">{policy.storeUser || EMPTY_VALUE}</div>
								</TableCell>
								<TableCell>
									<div className="max-w-[180px] truncate" title={policy.deviceLabel}>
										{policy.deviceLabel || EMPTY_VALUE}
									</div>
									<div className="text-xs text-neutral-500">{policy.deviceType || EMPTY_VALUE}</div>
								</TableCell>
								<TableCell>{policy.activeClaim || EMPTY_VALUE}</TableCell>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{loading && <TableLoadingOverlay />}
		</div>
	);
}

function ClaimsTable({
	claims,
	loading,
	updatingClaimId,
	letterGeneratingId,
	onUpdateStatus,
	onDownloadLetter
}: {
	claims: InsuranceClaimRow[];
	loading: boolean;
	updatingClaimId: string | null;
	letterGeneratingId: string | null;
	onUpdateStatus: (claim: InsuranceClaimRow, status: ClaimDecisionStatus) => void;
	onDownloadLetter: (claim: InsuranceClaimRow) => void;
}) {
	const [statusMenuClaimId, setStatusMenuClaimId] = useState<string | null>(null);

	if (!claims.length) {
		return (
			<div className="relative min-h-[260px] overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900/50">
				<EmptyState
					icon={FileWarning}
					title="No hay siniestros para mostrar"
					subtitle="Ajuste los filtros o cambie el país seleccionado."
				/>
				{loading && <TableLoadingOverlay />}
			</div>
		);
	}

	return (
		<div className="relative overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900/50">
			<div className="overflow-x-auto">
				<table className="w-full min-w-[1660px] text-left text-sm">
					<thead className="border-b border-neutral-800 bg-neutral-950 text-xs uppercase text-neutral-400">
						<tr>
							<TableHeader>Siniestro</TableHeader>
							<TableHeader>Estado</TableHeader>
							<TableHeader>Póliza</TableHeader>
							<TableHeader>Cliente</TableHeader>
							<TableHeader>Equipo</TableHeader>
							<TableHeader>IMEI</TableHeader>
							<TableHeader>Ocurrencia</TableHeader>
							<TableHeader>Creado</TableHeader>
							<TableHeader>Actualizado</TableHeader>
							<TableHeader>Información</TableHeader>
							<TableHeader>Soporte</TableHeader>
							<TableHeader>Acciones</TableHeader>
						</tr>
					</thead>
					<tbody className="divide-y divide-neutral-800/80">
						{claims.map((claim) => (
							<tr key={claim.id} className="text-neutral-200 transition-colors hover:bg-neutral-800/30">
								<TableCell>
									<div className="font-medium text-neutral-100">{claim.siniestroId || claim.id}</div>
									<div className="text-xs text-neutral-500">{claim.type || EMPTY_VALUE}</div>
								</TableCell>
								<TableCell>
									<div className="relative inline-flex items-center gap-2">
										<StatusBadge status={claim.status}>{claim.statusLabel}</StatusBadge>
										{claim.status !== 'APROBADO' && claim.status !== 'RECHAZADO' && (
											<button
												className="rounded-md p-1 text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-neutral-100 disabled:opacity-40"
												disabled={loading || updatingClaimId === claim.id}
												onClick={() => setStatusMenuClaimId(statusMenuClaimId === claim.id ? null : claim.id)}
												title="Cambiar estado"
												type="button"
											>
												<Pencil className="size-4" />
											</button>
										)}
										{statusMenuClaimId === claim.id && (
											<div className="absolute left-0 top-8 z-30 w-36 rounded-lg border border-neutral-700 bg-neutral-950 p-1 shadow-lg">
												<button
													className="w-full rounded-md px-3 py-2 text-left text-sm text-neutral-200 hover:bg-neutral-800"
													onClick={() => {
														setStatusMenuClaimId(null);
														onUpdateStatus(claim, 'APROBADO');
													}}
													type="button"
												>
													Aprobar
												</button>
												<button
													className="w-full rounded-md px-3 py-2 text-left text-sm text-red-300 hover:bg-neutral-800"
													onClick={() => {
														setStatusMenuClaimId(null);
														onUpdateStatus(claim, 'RECHAZADO');
													}}
													type="button"
												>
													Rechazar
												</button>
											</div>
										)}
									</div>
								</TableCell>
								<TableCell>{claim.policyNumber || EMPTY_VALUE}</TableCell>
								<TableCell>
									<div className="max-w-[220px] truncate" title={claim.customerEmail}>
										{claim.customerEmail || EMPTY_VALUE}
									</div>
									<div className="text-xs text-neutral-500">Usuario: {claim.phone || EMPTY_VALUE}</div>
									{claim.secondaryPhoneNumber && <div className="text-xs text-neutral-500">Secundario: {claim.secondaryPhoneNumber}</div>}
								</TableCell>
								<TableCell>
									<div className="max-w-[180px] truncate" title={claim.deviceLabel}>
										{claim.deviceLabel || EMPTY_VALUE}
									</div>
									<div className="text-xs text-neutral-500">{claim.deviceId || EMPTY_VALUE}</div>
								</TableCell>
								<TableCell>{claim.deviceImei || EMPTY_VALUE}</TableCell>
								<TableCell>
									<div>{claim.occurredAt || EMPTY_VALUE}</div>
									<div className="max-w-[170px] truncate text-xs text-neutral-500" title={claim.place}>
										{claim.place || EMPTY_VALUE}
									</div>
								</TableCell>
								<TableCell>{formatDateTime(claim.createdAt)}</TableCell>
								<TableCell>
									<div>{formatDateTime(claim.updatedAt)}</div>
									{claim.resolvedAt && <div className="text-xs text-neutral-500">Resuelto {formatDate(claim.resolvedAt)}</div>}
								</TableCell>
								<TableCell>
									<div className="max-w-[280px] truncate" title={claim.description}>
										{claim.description || EMPTY_VALUE}
									</div>
									<div className="max-w-[280px] truncate text-xs text-neutral-500" title={claim.observations}>
										{claim.observations || EMPTY_VALUE}
									</div>
									{claim.approvedAmount && <div className="text-xs text-neutral-500">Monto aprobado: {claim.approvedAmount}</div>}
								</TableCell>
								<TableCell>
									{claim.supportUrl ? (
										<a
											className="text-primary-400 hover:text-primary-300 hover:underline"
											href={claim.supportUrl}
											rel="noreferrer"
											target="_blank"
										>
											Ver soporte
										</a>
									) : (
										EMPTY_VALUE
									)}
								</TableCell>
								<TableCell>
									<ClaimLetterAction
										claim={claim}
										loading={loading}
										letterLoading={letterGeneratingId === claim.id}
										onDownloadLetter={onDownloadLetter}
									/>
								</TableCell>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{loading && <TableLoadingOverlay />}
		</div>
	);
}

function ClaimLetterAction({
	claim,
	loading,
	letterLoading,
	onDownloadLetter
}: {
	claim: InsuranceClaimRow;
	loading: boolean;
	letterLoading: boolean;
	onDownloadLetter: (claim: InsuranceClaimRow) => void;
}) {
	return (
		<Button
			className="!h-9 !w-auto px-3"
			disabled={loading}
			loading={letterLoading}
			variant="secondary"
			Icon={<FileText className="size-4" />}
			onClick={() => onDownloadLetter(claim)}
			title="Generar carta de desincorporación"
		>
			Carta PDF
		</Button>
	);
}

function downloadBase64File(base64: string, filename: string, contentType: string) {
	const byteCharacters = window.atob(base64);
	const byteNumbers = new Array(byteCharacters.length);
	for (let i = 0; i < byteCharacters.length; i += 1) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}
	const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
	const url = window.URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = filename;
	document.body.appendChild(anchor);
	anchor.click();
	window.URL.revokeObjectURL(url);
	document.body.removeChild(anchor);
}

function TableLoadingOverlay() {
	return (
		<div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-black/50">
			<Spinner size={2.75} />
		</div>
	);
}

function TableHeader({ children }: { children: React.ReactNode }) {
	return <th className="whitespace-nowrap px-4 py-3 font-semibold">{children}</th>;
}

function TableCell({ children }: { children: React.ReactNode }) {
	return <td className="align-top px-4 py-3">{children}</td>;
}

function StatusBadge({ status, children }: { status: string; children: React.ReactNode }) {
	const normalized = status.toUpperCase();
	const className =
		normalized === 'ACTIVE' || normalized === 'APROBADO'
			? 'bg-emerald-500/10 text-emerald-400'
			: normalized === 'RECIBIDO' || normalized === 'EN_ANALISIS' || normalized === 'PENDIENTE_INFO'
				? 'bg-yellow-500/10 text-yellow-400'
				: normalized === 'RECHAZADO' || normalized === 'EXPIRED' || normalized === 'VENCIDA'
					? 'bg-red-500/10 text-red-400'
					: 'bg-neutral-500/10 text-neutral-400';

	return <span className={cn('inline-flex rounded-md px-2 py-1 text-xs font-medium', className)}>{children}</span>;
}

function getPageSubtitle(count: number, pageIndex: number, pageSize: number, label: string) {
	if (!count) return `0 ${label}`;
	const firstRow = pageIndex * pageSize + 1;
	const lastRow = pageIndex * pageSize + count;
	return `Mostrando ${firstRow}-${lastRow} ${label}`;
}

function formatDateTime(value: string | null) {
	const date = parseDate(value);
	if (!date) return EMPTY_VALUE;
	return new Intl.DateTimeFormat('es-VE', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	}).format(date);
}

function formatDate(value: string | null) {
	const date = parseDate(value);
	if (!date) return EMPTY_VALUE;
	return new Intl.DateTimeFormat('es-VE', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	}).format(date);
}

function parseDate(value: string | null) {
	if (!value) return null;
	const parsedDate = new Date(value);
	return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function getCountryLabel(country: string) {
	return COUNTRY_LABELS[country as keyof typeof COUNTRY_LABELS] || country || 'país seleccionado';
}
